import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "./SEO";

const FaqSection = () => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqs = [
    {
      question:
        "Accommodation details for the people attending the conference/workshop:",
      answer:
        "Accommodation arrangements will be provided on the Sharda University campus for outstation participants. Details regarding room types, availability, and booking procedures will be shared upon registration confirmation.",
    },
    {
      question: "Food:",
      answer:
        "Meals will be provided to all registered participants during the conference days. The conference fee includes breakfast, lunch, and dinner. Special dietary requirements can be accommodated upon prior request.",
    },
    {
      question:
        "What does the accompanying person registration amount include:",
      answer:
        "The accompanying person registration covers accommodation, meals, and participation in social events and cultural programs organized during the conference.",
    },
    {
      question: "Stay options near to Sharda University:",
      answer:
        "Several hotels and guest houses are available near the Sharda University campus in Greater Noida. A curated list of recommended stay options with contact details will be shared with registered participants.",
    },
    {
      question: "Expected climatic conditions during the conference",
      answer:
        "November in Greater Noida is generally pleasant with mild weather. Temperatures typically range from 10°C to 25°C. Light woolen clothing is recommended for the evenings.",
    },
    {
      question: "Reimbursement possibilities for travel expenses:",
      answer:
        "Limited travel support may be available for selected student participants and early career researchers. Details will be announced closer to the conference date. Applicants should apply through the official registration portal.",
    },
  ];

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-sm font-bold text-indigo-600 tracking-wider uppercase mb-2">
            Support
          </h2>
          <h3 className="text-3xl md:text-4xl font-extrabold text-[#1e1b4b]">
            Frequently Asked Questions (FAQs)
          </h3>
          <div className="w-24 h-1 bg-indigo-600 mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
            >
              <button
                className="w-full flex items-center gap-4 px-6 py-5 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="w-8 h-8 flex-shrink-0 bg-slate-100 text-slate-500 rounded-lg flex items-center justify-center font-semibold text-sm">
                  {i + 1}
                </span>
                <span className="flex-1 font-semibold text-[#1e1b4b] text-sm md:text-base">
                  {faq.question}
                </span>
                <span className="flex-shrink-0 text-red-500 text-xl font-light">
                  {openIndex === i ? "−" : "+"}
                </span>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5 pl-[4.5rem]">
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
const Hero = () => {
  const [timeLeft, setTimeLeft] = React.useState({});

  useEffect(() => {
    const target = new Date("2026-11-19T00:00:00");

    const interval = setInterval(() => {
      const now = new Date();
      const diff = target - now;

      if (diff <= 0) {
        clearInterval(interval);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-slate-50">
      <SEO title="Home" />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-1000 scale-105"
          style={{
            backgroundImage: `url('/nclc-2026/assets/hero-image.webp')`,
          }}
        >
          {/* Enhanced Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1e1b4b]/90 via-[#1e1b4b]/80 to-indigo-900/70"></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white pt-24">
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-10 duration-1000 fill-mode-both">
            <div className="flex items-center justify-center gap-3 mb-2">
              {[
                { label: "Days", value: timeLeft.days },
                { label: "Hours", value: timeLeft.hours },
                { label: "Mins", value: timeLeft.minutes },
                { label: "Secs", value: timeLeft.seconds },
              ].map(({ label, value }, i) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 min-w-[70px]">
                    <span className="text-3xl md:text-4xl font-extrabold text-white tabular-nums leading-none">
                      {String(value ?? 0).padStart(2, "0")}
                    </span>
                    <span className="text-indigo-200 text-xs font-semibold uppercase tracking-widest mt-1">
                      {label}
                    </span>
                  </div>
                  {i < 3 && (
                    <span className="text-2xl font-bold text-indigo-300 -mt-4">
                      :
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="date-container">
              <svg className="border-svg" viewBox="0 0 500 80">
                <rect
                  x="4"
                  y="4"
                  width="492"
                  height="72"
                  rx="36"
                  ry="36"
                  className="flow-border"
                />
              </svg>

              <div className="date-badge">
                November 19, 2026 – November 21, 2026
              </div>
            </div>
            <h1 className="text-5xl md:text-5xl font-extrabold tracking-tight leading-tight">
              33<sup>rd</sup> National Conference{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-cyan-300">
                on Liquid Crystals
              </span>
              <br className="hidden md:block" />{" "}
              <span className="font-normal">(NCLC 2026)</span>
            </h1>
            {/* <span className="inline-block py-1 px-4 bg-indigo-500/30 border border-indigo-400/50 rounded-full text-indigo-100 text-lg font-semibold tracking-wider mb-4 backdrop-blur-sm">
              November 19-21, 2026
            </span> */}
            <div className="max-w-4xl mx-auto space-y-2">
              <p className="text-xl md:text-2xl text-indigo-100 leading-relaxed font-light">
                Hosted by{" "}
                <a
                  href="https://www.sharda.ac.in/department/environmental-sciences/"
                  className="hover:underline"
                >
                  <span className="font-semibold text-white">
                    Department of Physics & Environmental Sciences
                  </span>
                </a>
                ,
                <br /> Sharda School of Engineering & Science (SSES), Sharda
                University, Greater Noida - 201310, India.
              </p>
              <p className="text-lg md:text-xl text-indigo-200 leading-relaxed font-light">
                In association with the{" "}
                <a href="https://ilcs.in/" className="hover:underline">
                  <span className="font-semibold text-white">
                    Indian Liquid Crystal Society (ILCS)
                  </span>
                </a>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-6 justify-center">
              <img
                src="/nclc-2026/assets/Hexon-300x118.png.webp"
                alt="Hexon Logo"
                className="h-20 w-auto object-contain bg-white transition-all duration-300"
              />
              <img
                src="/nclc-2026/assets/iit-roorkee.png"
                alt="IIT Roorkee Logo"
                className="h-20 w-auto object-contain bg-white transition-all duration-300 "
              />
              <img
                src="/nclc-2026/assets/rsoc.webp"
                alt="Royal Society of Chemistry Logo"
                className="h-20 w-auto object-contain bg-white transition-all duration-300"
              />
              <img
                src="/nclc-2026/assets/Picture1.png"
                alt="Sponsor 1"
                className="h-20 w-auto object-contain bg-white transition-all duration-300"
              />
              <img
                src="/nclc-2026/assets/Picture2.jpg"
                alt="Sponsor 2"
                className="h-20 w-auto object-contain bg-white transition-all duration-300"
              />
            </div>
            <div className="pt-6 pb-2 max-w-5xl mx-auto w-full gap-6">
              {/* President */}
              <div className="flex justify-center mb-4 gap-6">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-3 text-center transform transition-transform hover:scale-105">
                  <p className="text-indigo-200 text-sm font-bold uppercase tracking-wider mb-1">
                    Convener
                  </p>
                  <p className="text-white font-bold text-lg">
                    Dr. Pankaj Kumar Tripathi
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-3 text-center transform transition-transform hover:scale-105">
                  <p className="text-indigo-200 text-sm font-bold uppercase tracking-wider mb-1">
                    Co-Convener
                  </p>
                  <p className="text-white font-bold text-lg">
                    Prof. (Dr.) Mohit Sahni
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-3 text-center transform transition-transform hover:scale-105">
                  <p className="text-indigo-200 text-sm font-bold uppercase tracking-wider mb-1">
                    President of ILCS
                  </p>
                  <p className="text-white font-bold text-lg">
                    Prof. (Dr.) Sandeep Kumar
                  </p>
                </div>
              </div>
            </div>

            <div className="pb-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/registration"
                className="px-6 py-2.5 bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold rounded-lg transition-all shadow-md hover:shadow-indigo-500/30 transform hover:-translate-y-0.5"
              >
                Register Now
              </Link>
              <Link
                to="/abstract-submission"
                className="px-6 py-2.5 bg-white/10 backdrop-blur-sm border border-indigo-300/60 text-indigo-100 hover:bg-white/20 text-sm font-semibold rounded-lg transition-all transform hover:-translate-y-0.5"
              >
                Submit Abstract
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Wave/Curve */}
        {/* <div className="absolute bottom- left-0 w-full overflow-hidden leading-none z-10">
          <svg
            className="relative block w-full h-[30px]"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118,130.95,123,194,106.8,248.51,92.65,285.5,67.8,321.39,56.44Z"
              fill="#f8fafc"
            ></path>
          </svg>
        </div> */}
      </section>

      {/* About The Conference */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-sm font-bold text-indigo-600 tracking-wider uppercase mb-2">
                About The Event
              </h2>
              <h3 className="text-3xl md:text-4xl font-extrabold text-[#1e1b4b] mb-6">
                33<sup>rd</sup> National Conference on Liquid Crystals
                (NCLC-2026)
              </h3>
              <p className="text-md text-gray-600 mb-4 leading-relaxed text-justify">
                The National Conference on Liquid Crystals (NCLC), organized
                annually by the Indian Liquid Crystal Society (ILCS), is a
                prominent scientific event that has been held regularly at
                various universities and research institutes across India since
                its inception in 1991. The conference serves as an important
                platform for researchers, scientists, academicians, students and
                industry working in the fields of liquid crystals, soft
                condensed matter, and related interdisciplinary areas. It
                attracts leading national and international experts from all
                over the country, along with a large number of research scholars
                and postgraduate students, with the aim of promoting the
                exchange of scientific knowledge, presenting recent research
                developments, and encouraging collaboration among researchers.
              </p>
              <p className="text-md text-gray-600 mb-8 leading-relaxed text-justify">
                The program typically includes keynote lectures, invited talks,
                oral presentations, and poster sessions that cover both
                fundamental studies and emerging technological applications of
                liquid crystal materials. In addition, the conference provides
                young researchers with an opportunity to present their work,
                interact with experienced scientists, receive constructive
                feedback, and build professional networks. Through these
                activities and the recognition of outstanding contributions,
                NCLC plays a significant role in strengthening research
                interaction and advancing innovation in the field of liquid
                crystals and soft matter.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="border-l-4 border-indigo-500 pl-4">
                  <p className="text-3xl font-bold text-[#1e1b4b]">50+</p>
                  <p className="text-gray-500 font-medium">Invited Speakers</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-3xl font-bold text-[#1e1b4b]">3</p>
                  <p className="text-gray-500 font-medium">
                    Days of Networking
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-indigo-600 transform translate-x-4 translate-y-4 rounded-2xl opacity-20"></div>
              <img
                src="/nclc-2026/assets/department-of-physics-environmental-sciences-quantum-carnival-2026_.webp"
                alt="eventimage"
                className="relative rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <h4 className="font-bold text-[#1e1b4b] mb-2">Venue</h4>
                <p className="text-gray-600 text-sm">
                  Sharda University Campus, Knowledge Park III, Greater Noida.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-indigo-50/50 blur-3xl z-0"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-blue-50/50 blur-3xl z-0"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold text-indigo-600 tracking-wider uppercase mb-2">
              Our Department
            </h2>
            <h3 className="text-3xl md:text-4xl font-extrabold text-[#1e1b4b]">
              Physics &amp; Environmental Sciences
            </h3>
            <div className="w-24 h-1 bg-indigo-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed font-light">
                <span className="font-semibold text-indigo-600">
                  The Department of Physics &amp; Environmental Sciences
                </span>{" "}
                is a vibrant and interdisciplinary hub dedicated to nurturing
                globally competent scientists, researchers, educators, and
                professionals.
              </p>

              <div className="bg-slate-50 border-l-4 border-indigo-500 p-6 rounded-r-xl shadow-sm">
                <p className="text-gray-600 leading-relaxed italic">
                  With a commitment to academic excellence, sustainability, and
                  innovation, the department integrates physical sciences with
                  environmental stewardship to address global challenges through
                  scientific inquiry, AI, and technological advancement.
                </p>
              </div>

              <p className="text-md text-gray-600 leading-relaxed">
                With over{" "}
                <strong className="text-gray-900">
                  25 highly experienced and research-driven faculty members
                </strong>{" "}
                trained at premier institutions, the department excels in
                teaching and research. We offer a wide range of undergraduate,
                postgraduate, and doctoral programmes to develop core
                competencies.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-center space-x-3 p-4 bg-white shadow-sm border border-gray-100 rounded-xl hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                      />
                    </svg>
                  </div>
                  <span className="font-semibold text-gray-800">
                    Advanced Research
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white shadow-sm border border-gray-100 rounded-xl hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <span className="font-semibold text-gray-800">
                    Sustainability
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative mt-8 lg:mt-0">
              <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500 to-cyan-400 transform translate-x-3 translate-y-3 rounded-2xl opacity-30"></div>
              <div className="bg-white rounded-2xl shadow-xl relative overflow-hidden border border-gray-100">
                <img
                  src="/nclc-2026/assets/departmentimage.webp"
                  alt="Laboratory"
                  className="w-full h-56 object-cover"
                />
                <div className="p-6">
                  <h4 className="font-bold text-lg text-[#1e1b4b] mb-3">
                    Key Research Domains
                  </h4>
                  <ul className="space-y-3 text-sm text-gray-600">
                    <li className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-indigo-500 mr-3"></span>
                      Solar Cell Tech &amp; Supercapacitors
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-indigo-500 mr-3"></span>
                      Liquid Crystals &amp; Photonics
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-indigo-500 mr-3"></span>
                      Nanoscience and Nanotechnology
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-indigo-500 mr-3"></span>
                      Quantum Computing, Sensing, and Communications
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 rounded-full bg-indigo-500 mr-3"></span>
                      Artificial Intelligence (AI) &amp; Biomedical Imaging
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0f172a] text-slate-300 relative overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-indigo-600/20 rounded-full blur-3xl mix-blend-screen pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl mix-blend-screen pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Visual Column */}
            <div className="order-2 lg:order-1 relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-cyan-400 transform -translate-x-4 -translate-y-4 rounded-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-500 blur-sm"></div>
              <div className="bg-white p-10 rounded-3xl shadow-2xl relative flex flex-col items-center justify-center min-h-[400px]">
                <img
                  src="/nclc-2026/assets/sharda-logo.webp"
                  alt="Sharda University Logo"
                  className="w-4/5 max-w-sm drop-shadow-md mb-8 transform transition-transform duration-700 group-hover:scale-105"
                />
                <div className="grid grid-cols-2 gap-4 w-full">
                  <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                    <span className="block text-2xl font-black text-indigo-600 mb-1">
                      A+
                    </span>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                      NAAC Accredited
                    </span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl text-center border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                    <span className="block text-2xl font-black text-indigo-600 mb-1">
                      87
                    </span>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                      NIRF India Rank
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="order-1 lg:order-2">
              <h2 className="text-sm font-bold text-cyan-400 tracking-wider uppercase mb-3 flex items-center">
                <span className="w-8 h-1 bg-cyan-400 mr-3 rounded-full"></span>{" "}
                About The University
              </h2>
              <h3 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                Sharda University
              </h3>

              <div className="space-y-6 text-slate-300">
                <p className="text-lg leading-relaxed ">
                  A flagship institution of the Sharda Group, guided by
                  principles inspired by Jain philosophy, emphasizing ethics,
                  education, and social responsibility. Established in 2009, it
                  has grown into one of India's leading centers for higher
                  education, research, and innovation.
                </p>
                <p className="leading-relaxed font-light">
                  Recognized for academic excellence, Sharda University has been
                  ranked among the top institutions in South Asia in the{" "}
                  <strong className="text-white font-semibold">
                    QS Asia University Rankings 2024
                  </strong>
                  . Beyond these achievements, it is known for its global
                  environment, hosting students from over{" "}
                  <strong className="text-white font-semibold">
                    95 countries
                  </strong>
                  .
                </p>

                <div className="bg-white/5 border border-white/10 p-5 rounded-xl mt-6 backdrop-blur-sm">
                  <h4 className="text-white font-bold mb-2 flex items-center">
                    <svg
                      className="w-5 h-5 text-cyan-400 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    The Philosophy of Sharda
                  </h4>
                  <p className="text-sm font-light leading-relaxed text-justify">
                    The name "Sharda" represents Goddess Saraswati. The lotus in
                    our logo signifies the seat of learning, its varying colors
                    reflecting our diverse disciplines. The overlapping petals
                    illustrate collaboration across nationalities, while petals
                    rising toward a star symbolize our mission of education
                    without boundaries.
                  </p>
                </div>
                <div className="mt-10">
                  <p>
                    Website link:{" "}
                    <a
                      href="https://www.sharda.ac.in/"
                      className="hover:underline"
                    >
                      https://www.sharda.ac.in/
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights/Themes Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold text-indigo-600 tracking-wider uppercase mb-2">
              Conference Themes
            </h2>
            <h3 className="text-3xl md:text-4xl font-extrabold text-[#1e1b4b]">
              Key Areas of Discussion
            </h3>
            <div className="w-24 h-1 bg-indigo-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Synthesis and Characterization",
                desc: "Latest breakthroughs in synthesizing new liquid crystalline materials and advanced characterization techniques.",
              },
              {
                title: "Phase Transitions",
                desc: "Understanding the complex phase behaviors, ordering, and transitions in soft matter physics.",
              },
              {
                title: "Display Technologies",
                desc: "Innovations driving the next generation of LCDs, flexible displays, and optical devices.",
              },
              {
                title: "Biological Liquid Crystals",
                desc: "Exploring the intersections of liquid crystals with biological systems, membranes, and origins of life.",
              },
              {
                title: "Nanocomposites",
                desc: "Integration of nanoparticles into liquid crystals for enhanced physical and optical properties.",
              },
              {
                title: "Energy Applications",
                desc: "Role of liquid crystals in photovoltaics, smart windows, and energy harvesting materials.",
              },
            ].map((theme, i) => (
              <div
                key={i}
                className="bg-slate-50 p-8 rounded-2xl hover:bg-indigo-50 transition-colors duration-300 border border-slate-100"
              >
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center mb-6">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-[#1e1b4b] mb-3">
                  {theme.title}
                </h4>
                <p className="text-gray-600 leading-relaxed">{theme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Themes + RSC Awards Section */}
      <section className=" bg-white pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h3 className="text-3xl md:text-4xl font-extrabold text-[#1e1b4b]">
              Conference Themes
            </h3>
            <div className="w-24 h-1 bg-indigo-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Scientific Themes */}
            <div>
              <h4 className="text-2xl font-extrabold text-[#1e1b4b] mb-8 text-center">
                Scientific Themes
              </h4>
              <ul className="space-y-3">
                {[
                  "Molecular engineering and structural design of advanced liquid crystalline materials",
                  "Phase structures, defect dynamics, and topology in liquid crystal systems",
                  "Polar, ferroelectric, and emerging nematic phases in liquid crystals",
                  "Optical modulation, nonlinear optics, and light–matter interactions in LC media",
                  "Liquid crystal polymers, elastomers, and macromolecular LC systems",
                  "Bio-inspired, biological, and active liquid crystal materials",
                  "Nanostructured, colloidal, and hybrid LC composites with functional properties",
                  "Smart, stimuli-responsive, and photoactive liquid crystal systems",
                  "Tunable photonic devices, liquid crystal lasers, and metamaterials",
                  "Liquid crystal–based sensors, actuators, and soft robotic systems",
                  "Energy-efficient devices, smart windows, and adaptive optical elements using LCs",
                  "Sustainable materials and environmentally responsive LC technologies",
                  "Emerging interdisciplinary applications of liquid crystals in materials science and engineering",
                ].map((theme, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                    <span className="text-amber-600 font-medium leading-relaxed">
                      {theme}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* RSC Sponsored Awards */}
            <div>
              <h4 className="text-2xl font-extrabold text-[#1e1b4b] mb-8 text-center">
                RSC Sponsored Awards at NCLC 2026
              </h4>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-6">
                  We are delighted to announce that the{" "}
                  <strong className="text-[#1e1b4b]">
                    Royal Society of Chemistry (RSC)
                  </strong>{" "}
                  will sponsor{" "}
                  <strong className="text-[#1e1b4b]">
                    presentation awards
                  </strong>{" "}
                  at the{" "}
                  <strong className="text-[#1e1b4b]">
                    33rd National Conference on Liquid Crystals (NCLC-2026)
                  </strong>
                  , to be held at Sharda University, Greater Noida, from{" "}
                  <strong className="text-[#1e1b4b]">
                    19–21 November 2026
                  </strong>
                  .
                </p>

                <p className="font-bold text-[#1e1b4b] mb-4">Awards include:</p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-4 bg-white border border-amber-100 rounded-xl p-4 shadow-sm">
                    <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      2
                    </div>
                    <div>
                      <p className="font-semibold text-[#1e1b4b]">
                        Best Oral Presentation Awards
                      </p>
                      <p className="text-sm text-gray-500">
                        Early Career Researchers, Age &lt; 35 yrs — ₹5,000 each
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 bg-white border border-amber-100 rounded-xl p-4 shadow-sm">
                    <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      6
                    </div>
                    <div>
                      <p className="font-semibold text-[#1e1b4b]">
                        Best Poster Presentation Awards
                      </p>
                      <p className="text-sm text-gray-500">₹3,000 each</p>
                    </div>
                  </div>
                </div>

                <p className="text-gray-600 leading-relaxed text-sm mb-4">
                  These awards aim to recognize{" "}
                  <strong className="text-gray-800">
                    outstanding scientific contributions
                  </strong>{" "}
                  from young researchers in{" "}
                  <strong className="text-gray-800">
                    liquid crystals, soft matter, and advanced materials
                  </strong>
                  .
                </p>
                <p className="text-gray-600 leading-relaxed text-sm">
                  We extend our sincere thanks to the{" "}
                  <strong className="text-gray-800">
                    Royal Society of Chemistry
                  </strong>{" "}
                  for supporting the research community through these
                  prestigious awards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-extrabold text-[#1e1b4b]">
              Our Sponsors
            </h3>
            <div className="w-24 h-1 bg-indigo-600 mx-auto mt-6 rounded-full"></div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {[
              { src: "/nclc-2026/assets/Hexon-300x118.png.webp", alt: "Hexon Logo" },
              { src: "/nclc-2026/assets/rsoc.webp", alt: "Royal Society of Chemistry Logo" },
              { src: "/nclc-2026/assets/iit-roorkee.png", alt: "IIT Roorkee Logo" },
              { src: "/nclc-2026/assets/Picture1.png", alt: "Sponsor 1" },
              { src: "/nclc-2026/assets/Picture2.jpg", alt: "Sponsor 2" },
            ].map((sponsor, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex items-center justify-center w-64 h-32 md:w-72 md:h-36">
                <img
                  src={sponsor.src}
                  alt={sponsor.alt}
                  className="max-h-20 md:max-h-24 max-w-full object-contain filter hover:brightness-110 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
      <FaqSection />
    </div>
  );
};

export default Hero;

